import { onSnapshot } from 'firebase/firestore';
import { useEffect, useRef } from 'react';

/**
 * Real-time listener manager
 * Prevents listener leaks and optimizes snapshot subscriptions
 */
class ListenerManager {
  constructor() {
    this.listeners = new Map();
    this.paused = false;
  }
  
  /**
   * Subscribe to a query with automatic cleanup
   */
  subscribe(key, query, callback, options = {}) {
    // Unsubscribe existing listener if it exists
    if (this.listeners.has(key)) {
      this.listeners.get(key).unsubscribe();
    }
    
    // Create wrapper callback that respects pause state
    const wrappedCallback = (...args) => {
      if (!this.paused || options.ignoreWhenPaused === false) {
        callback(...args);
      }
    };
    
    // Create the snapshot listener
    const unsubscribe = onSnapshot(
      query,
      wrappedCallback,
      (error) => {
        console.error(`[Listener Error] ${key}:`, error);
        if (options.onError) {
          options.onError(error);
        }
      }
    );
    
    // Store listener info
    this.listeners.set(key, {
      unsubscribe,
      query,
      callback,
      options,
      createdAt: new Date()
    });
    
    console.log(`[Listener] Subscribed to ${key}. Active listeners: ${this.listeners.size}`);
    
    return unsubscribe;
  }
  
  /**
   * Unsubscribe from a specific listener
   */
  unsubscribe(key) {
    if (this.listeners.has(key)) {
      const listener = this.listeners.get(key);
      listener.unsubscribe();
      this.listeners.delete(key);
      console.log(`[Listener] Unsubscribed from ${key}. Active listeners: ${this.listeners.size}`);
    }
  }
  
  /**
   * Unsubscribe from all listeners
   */
  unsubscribeAll() {
    const count = this.listeners.size;
    this.listeners.forEach((listener, key) => {
      listener.unsubscribe();
    });
    this.listeners.clear();
    console.log(`[Listener] Unsubscribed from ${count} listeners`);
  }
  
  /**
   * Pause all listeners (useful for background tabs)
   */
  pause() {
    this.paused = true;
    console.log(`[Listener] Paused ${this.listeners.size} listeners`);
  }
  
  /**
   * Resume all listeners
   */
  resume() {
    this.paused = false;
    console.log(`[Listener] Resumed ${this.listeners.size} listeners`);
  }
  
  /**
   * Get info about active listeners
   */
  getListenerInfo() {
    const info = [];
    this.listeners.forEach((listener, key) => {
      info.push({
        key,
        createdAt: listener.createdAt,
        paused: this.paused,
        options: listener.options
      });
    });
    return info;
  }
  
  /**
   * Check for potential memory leaks
   */
  auditListeners() {
    const now = new Date();
    const oldListeners = [];
    
    this.listeners.forEach((listener, key) => {
      const age = now - listener.createdAt;
      const ageMinutes = Math.floor(age / (1000 * 60));
      
      if (ageMinutes > 30) { // Flag listeners older than 30 minutes
        oldListeners.push({ key, ageMinutes });
      }
    });
    
    if (oldListeners.length > 0) {
      console.warn('[Listener Audit] Found potentially leaked listeners:', oldListeners);
    }
    
    return {
      totalListeners: this.listeners.size,
      oldListeners: oldListeners.length,
      details: oldListeners
    };
  }

  /**
   * Pause all listeners (e.g., when tab becomes inactive)
   */
  pauseListeners() {
    this.listeners.forEach(listener => {
      if (!listener.paused && listener.unsubscribe) {
        listener.unsubscribe();
        listener.paused = true;
      }
    });
    console.log(`⏸️ ListenerManager: Paused ${this.listeners.size} listeners`);
  }

  /**
   * Resume all paused listeners
   */
  resumeListeners() {
    this.listeners.forEach(listener => {
      if (listener.paused) {
        // Re-establish the listener
        const newUnsub = onSnapshot(
          listener.query,
          listener.callback,
          listener.errorCallback
        );
        listener.unsubscribe = newUnsub;
        listener.paused = false;
      }
    });
    console.log(`▶️ ListenerManager: Resumed ${this.listeners.size} listeners`);
  }

  /**
   * Get listener statistics
   */
  getStats() {
    return {
      activeListeners: this.listeners.size,
      pausedListeners: Array.from(this.listeners.values()).filter(l => l.paused).length,
      totalCreated: this.totalCreated,
      totalDestroyed: this.totalDestroyed,
      memoryLeaksDetected: this.memoryLeaksDetected
    };
  }
}

// Create global instance
const globalListenerManager = new ListenerManager();

export { ListenerManager, globalListenerManager };

/**
 * React hook for managing listeners with automatic cleanup
 */
export function useFirestoreListener(key, query, callback, dependencies = [], options = {}) {
  const callbackRef = useRef(callback);
  
  // Update callback ref when callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);
  
  useEffect(() => {
    if (!query) return;
    
    // Subscribe with current callback
    const unsubscribe = globalListenerManager.subscribe(
      key,
      query,
      (...args) => callbackRef.current(...args),
      options
    );
    
    // Cleanup on unmount or dependency change
    return () => {
      globalListenerManager.unsubscribe(key);
    };
  }, [key, query, ...dependencies]);
}

/**
 * Hook for tab visibility optimization
 */
export function useVisibilityListener(callback, deps = []) {
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === 'visible';
      callback(isVisible);
      
      // Pause/resume listeners based on visibility
      if (isVisible) {
        globalListenerManager.resume();
      } else {
        globalListenerManager.pause();
      }
    };
    
    // Initial call
    handleVisibilityChange();
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, deps);
}

/**
 * Hook for listener performance monitoring
 */
export function useListenerMonitoring(intervalMs = 60000) {
  useEffect(() => {
    const interval = setInterval(() => {
      const audit = globalListenerManager.auditListeners();
      
      if (audit.totalListeners > 10) {
        console.warn(`[Listener Monitor] High listener count: ${audit.totalListeners}`);
      }
      
      if (audit.oldListeners > 0) {
        console.warn(`[Listener Monitor] ${audit.oldListeners} potentially leaked listeners`);
      }
    }, intervalMs);
    
    return () => clearInterval(interval);
  }, [intervalMs]);
}

export default globalListenerManager;